import React, { useState, useEffect } from 'react';
import {
  Button,
  DatePicker,
  SearchableDropdown,
  SideBarModal,
} from '@/components';
import { ConfirmDialog } from '@components/ConfirmDialog';
import { Vehicle } from '@/services/api/vehicles';
import { vehiclesApi } from '@/services/api/vehicles';
import { getBusinessUnitLabel } from '@/types/common';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import {
  extractApiErrorMessage,
  openImageFileDialog,
  getBucketUrlImageV2,
  uploadFileImage,
  getUserEmail,
} from '@/utils';
import { v4 as uuidv4 } from 'uuid';
import ReturnVehicleDetailView from './ReturnVehicleDetailView';

type ReturnReason =
  | 'CONTRACT_END'
  | 'IDLE_AT_HUB_TOO_LONG'
  | 'VEHICLE_SWAP'
  | 'CLIENT_REQUEST'
  | 'OTHER';

interface ReturnVehicleModalProps {
  isOpen: boolean;
  vehicle: Vehicle | null;
  isEditMode: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onConfirmReturn?: (payload: ReturnVehicleSubmitData) => Promise<void>;
  allowedReturnReasons?: ReturnReason[];
}

interface FormData {
  returnDate: string;
  returnOdometer: string;
  returnCondition: 'GOOD' | 'MINOR_DAMAGE' | 'MAJOR_DAMAGE';
  returnReason: ReturnReason;
  returnReasonOther?: string;
  vehicleReturnImageURL: string;
  unitEquipment?: string[];
  returnUnitEquipment: string[];
  returnBastURL: string;
  returnOemRepName: string;
  returnOemRepPhone: string;
  returnIssues: string;
  returnNotes: string;
}

interface ReturnVehicleSubmitData {
  returnDate: string;
  returnOdometer?: number;
  returnUnitEquipment: string[];
  returnCondition: 'GOOD' | 'MINOR_DAMAGE' | 'MAJOR_DAMAGE';
  returnReason: ReturnReason;
  returnReasonOther?: string;
  vehicleReturnImageURL?: string;
  returnBastURL?: string;
  returnOemRepName?: string;
  returnOemRepPhone?: string;
  returnNotes?: string;
  returnIssues?: string;
  updatedBy: string;
}

const returnReasons: Array<{ value: ReturnReason; label: string }> = [
  { value: 'CONTRACT_END', label: 'Contract End' },
  { value: 'IDLE_AT_HUB_TOO_LONG', label: 'Idle at Hub for Too Long' },
  { value: 'VEHICLE_SWAP', label: 'Vehicle Swap' },
  { value: 'CLIENT_REQUEST', label: 'Client Request' },
  { value: 'OTHER', label: 'Other (please specify)' },
];

const vehicleConditions = [
  { value: 'GOOD', label: 'Good' },
  { value: 'MINOR_DAMAGE', label: 'Minor Damage' },
  { value: 'MAJOR_DAMAGE', label: 'Major Damage' },
];

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-gray-500 hover:text-red-500"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ReturnVehicleModal: React.FC<ReturnVehicleModalProps> = ({
  isOpen,
  vehicle,
  isEditMode,
  onClose,
  onSuccess,
  onConfirmReturn,
  allowedReturnReasons,
}) => {
  const defaultReturnReason: ReturnReason =
    allowedReturnReasons?.[0] || 'CONTRACT_END';
  const [formData, setFormData] = useState<FormData>({
    returnDate: new Date().toISOString().split('T')[0],
    returnOdometer: '',
    returnCondition: 'GOOD',
    returnReason: defaultReturnReason,
    returnReasonOther: '',
    vehicleReturnImageURL: '',
    unitEquipment: [],
    returnUnitEquipment: [],
    returnBastURL: '',
    returnOemRepName: '',
    returnOemRepPhone: '',
    returnIssues: '',
    returnNotes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetail, setIsDetail] = useState(isEditMode);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [thumbnail, setThumbnail] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [equipmentValidationError, setEquipmentValidationError] =
    useState<string>('');

  useEffect(() => {
    if (vehicle && isOpen) {
      console.log('Vehicle data:', vehicle);

      const vehicleData = vehicle as any; // Cast to access return fields
      const resolvedReturnReason: ReturnReason =
        allowedReturnReasons && allowedReturnReasons.length > 0
          ? allowedReturnReasons.includes(vehicleData.returnReason)
            ? vehicleData.returnReason
            : allowedReturnReasons[0]
          : vehicleData.returnReason || 'CONTRACT_END';

      setFormData({
        returnDate: vehicleData.returnDate
          ? new Date(vehicleData.returnDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        returnOdometer: vehicleData.returnOdometer
          ? vehicleData.returnOdometer.toString()
          : '',
        returnCondition: vehicleData.returnCondition || 'GOOD',
        returnReason: resolvedReturnReason,
        returnReasonOther: vehicleData.returnReasonOther || '',
        vehicleReturnImageURL: vehicleData.vehicleReturnImageURL || '',
        returnUnitEquipment: vehicleData.returnUnitEquipment || [],
        unitEquipment: vehicle.unitEquipment || [],
        returnBastURL: vehicleData.returnBastURL || '',
        returnOemRepName: vehicleData.returnOemRepName || '',
        returnOemRepPhone: vehicleData.returnOemRepPhone || '',
        returnIssues: vehicleData.returnIssues || '',
        returnNotes: vehicleData.returnNotes || '',
      });

      // Set thumbnail if vehicle return image exists
      if (vehicleData.vehicleReturnImageURL) {
        setThumbnail(vehicleData.vehicleReturnImageURL);
      }
    }
  }, [vehicle, isOpen, allowedReturnReasons]);

  const handleInputChange = (
    field: keyof FormData,
    value: string | string[]
  ) => {
    // Validate return odometer to prevent negative values
    if (field === 'returnOdometer' && typeof value === 'string') {
      const numericValue = parseFloat(value);
      if (numericValue < 0) {
        return; // Don't update if the value is negative
      }
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEquipmentToggle = (equipment: string) => {
    const updatedEquipment = formData.returnUnitEquipment.includes(equipment)
      ? formData.returnUnitEquipment.filter((item) => item !== equipment)
      : [...formData.returnUnitEquipment, equipment];

    // If unchecking "Other", remove all custom "Other - " items
    if (
      equipment === 'Other' &&
      formData.returnUnitEquipment.includes('Other')
    ) {
      const filteredEquipment = updatedEquipment.filter(
        (item) => !item.startsWith('Other - ')
      );
      handleInputChange('returnUnitEquipment', filteredEquipment);
    } else {
      handleInputChange('returnUnitEquipment', updatedEquipment);
    }

    // Clear validation error when equipment changes
    if (equipmentValidationError) {
      validateEquipmentSelection(updatedEquipment);
    }
  };

  const validateEquipmentSelection = (
    equipment: string[] = formData.returnUnitEquipment
  ) => {
    // Check if "Other" is selected but no custom items are added
    const hasOther = equipment.includes('Other');
    const customOtherItems = equipment.filter((item) =>
      item.startsWith('Other - ')
    );

    if (hasOther && customOtherItems.length === 0) {
      setEquipmentValidationError(
        'Please add at least one custom item when "Other" is selected, or uncheck "Other".'
      );
      return false;
    } else {
      setEquipmentValidationError('');
      return true;
    }
  };

  const handleImageUpload = async () => {
    openImageFileDialog(
      async (file: { name: string; base64: string; size: number } | null) => {
        try {
          setIsImageUploading(true);

          if (!file) {
            setIsImageUploading(false);
            return;
          }

          const uuid = uuidv4();

          const response = await getBucketUrlImageV2({
            type: 'vehicleReturn',
            uuid: uuid,
          });

          if (!response?.data) {
            throw new Error('Failed to get upload URL');
          }

          const url = response.data;
          const base64Data = file?.base64?.split(',')[1];

          if (!base64Data) {
            throw new Error('Invalid file data');
          }

          const upload = await uploadFileImage(url, base64Data, file?.name);

          if (upload.status === 200) {
            setThumbnail(url);
            handleInputChange('vehicleReturnImageURL', url);
            showSuccessToast('Success', 'Upload image success');
          } else {
            throw new Error('Upload failed');
          }
        } catch (error: any) {
          const errorMessage = extractApiErrorMessage(
            error,
            'Failed to upload image'
          );
          showErrorToast('Error', errorMessage);
        } finally {
          setIsImageUploading(false);
        }
      }
    );
  };

  const handleSubmit = () => {
    // Validate equipment selection before proceeding
    const isEquipmentValid = validateEquipmentSelection();
    if (!isEquipmentValid) {
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      setIsSubmitting(true);
      setShowConfirmModal(false);

      if (!vehicle?.ID) {
        throw new Error('Vehicle ID is required');
      }

      const userEmail = getUserEmail();

      const submitData: ReturnVehicleSubmitData = {
        returnDate: new Date(formData.returnDate).toISOString(),
        returnOdometer: formData.returnOdometer
          ? parseInt(formData.returnOdometer, 10)
          : undefined,
        returnUnitEquipment: formData.returnUnitEquipment,
        returnCondition: formData.returnCondition,
        returnReason: formData.returnReason,
        returnReasonOther: formData.returnReasonOther || undefined,
        vehicleReturnImageURL: formData.vehicleReturnImageURL || undefined,
        returnBastURL: formData.returnBastURL || undefined,
        returnOemRepName: formData.returnOemRepName || undefined,
        returnOemRepPhone: formData.returnOemRepPhone || undefined,
        returnNotes: formData.returnNotes || undefined,
        returnIssues: formData.returnIssues || undefined,
        updatedBy: userEmail,
      };

      if (onConfirmReturn) {
        await onConfirmReturn(submitData);
        // showSuccessToast('Success', 'Vehicle returned successfully');
        onSuccess();
        handleClose();
        return;
      }

      // Use different API endpoints based on edit mode
      if (isEditMode) {
        await vehiclesApi.editReturnVehicle(vehicle.ID, submitData);
      } else {
        await vehiclesApi.returnVehicle(vehicle.ID, submitData);
      }

      showSuccessToast(
        'Success',
        isEditMode
          ? 'Vehicle return data updated successfully'
          : 'Vehicle returned successfully'
      );
      onSuccess();
      handleClose();
    } catch (error) {
      const fallbackMessage = onConfirmReturn
        ? 'Failed to return vehicle'
        : isEditMode
          ? 'Failed to update vehicle return data'
          : 'Failed to return vehicle';
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      showErrorToast('Error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      returnDate: new Date().toISOString().split('T')[0],
      returnOdometer: '',
      returnCondition: 'GOOD',
      returnReason: 'CONTRACT_END',
      returnReasonOther: '',
      vehicleReturnImageURL: '',
      returnUnitEquipment: [],
      returnBastURL: '',
      returnOemRepName: '',
      returnOemRepPhone: '',
      returnIssues: '',
      returnNotes: '',
    });
    setThumbnail('');
    setEquipmentValidationError('');
    setShowConfirmModal(false);
    setShowCancelConfirm(false);
    onClose();
  };

  if (!isOpen || !vehicle) return null;

  console.log('ReturnHandoverModal formData:', formData);

  const visibleReturnReasons = allowedReturnReasons?.length
    ? returnReasons.filter((reason) =>
        allowedReturnReasons.includes(reason.value)
      )
    : returnReasons;

  return (
    <>
      <SideBarModal
        isOpen={isOpen}
        onClose={handleClose}
        position="right"
        width={isDetail ? 'sm' : 'lg'}
        header={
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Return Vehicle to OEM
            </h2>
            <p className="text-sm font-normal text-gray-600">
              Complete all required information before returning vehicles.
            </p>
          </div>
        }
        footer={
          isDetail ? (
            <div className="flex">
              <Button
                variant="primary"
                onClick={() => setIsDetail(false)}
                className="flex-1 py-3"
              >
                Edit
              </Button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="neutral"
                onClick={() => setShowCancelConfirm(true)}
                className="flex-1 py-3"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                className="flex-1 py-3"
                disabled={
                  isSubmitting || isImageUploading || !!equipmentValidationError
                }
              >
                {isSubmitting
                  ? 'Processing...'
                  : isEditMode
                    ? 'Update Data'
                    : 'Confirm Return'}
              </Button>
            </div>
          )
        }
      >
        {isDetail ? (
          <ReturnVehicleDetailView initialData={vehicle} />
        ) : (
          <div className="flex-1 overflow-hidden flex">
            {/* Left Side - Unit Lists */}
            <div className="w-64 border-r border-gray-200 flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Vehicle Lists
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Select a vehicle to review its details.
                  </p>
                </div>

                {/* Vehicle Info Card */}
                <div className="border-2 border-[#5e2aac] rounded-lg p-3">
                  <div className="flex flex-col w-full">
                    <div className="text-sm font-semibold text-gray-900 pb-2 border-b border-gray-200 mb-3">
                      {vehicle.platNomor || 'Vehicle'}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col">
                        <div className="text-xs text-gray-500 mb-1">
                          OEM Name
                        </div>
                        <div className="text-xs font-semibold text-gray-900 break-words">
                          {vehicle.oemName || '-'}
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <div className="text-xs text-gray-500 mb-1">
                          Model Name
                        </div>
                        <div className="text-xs font-semibold text-gray-900 break-words">
                          {vehicle.modelName || '-'}
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <div className="text-xs text-gray-500 mb-1">
                          Hub Location
                        </div>
                        <div className="text-xs font-semibold text-gray-900 break-words">
                          {vehicle.hubName || '-'}
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <div className="text-xs text-gray-500 mb-1">
                          Business Unit
                        </div>
                        <div className="text-xs font-semibold text-gray-900 break-words">
                          {getBusinessUnitLabel(vehicle.businessUnit)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Unit Details */}
            <div className="flex-1 overflow-y-auto">
              <div className="">
                <div className="mt-2 px-6">
                  <p className="text-[18px] font-medium text-gray-900">
                    Vehicle Details
                  </p>
                  <p className="text-[14px] text-gray-500 mb-4">
                    Review the equipment return date.
                  </p>
                </div>

                {/* Return Info */}
                {/* Return Odometer */}

                <div className="bg-gray-50 px-6 py-2 text-xs font-medium text-gray-400 mb-4">
                  Return Info
                </div>
                <div className="px-6 space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Odometer at Return <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      value={formData.returnOdometer}
                      onChange={(e) =>
                        handleInputChange('returnOdometer', e.target.value)
                      }
                      placeholder="e.g. 50,000"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
                      disabled={isEditMode}
                    />
                    <span className="absolute right-3 top-2 text-sm text-gray-500">
                      km
                    </span>
                  </div>
                </div>

                <div className="space-y-6 px-6 mt-4">
                  {/* Return Date */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Return Date & Time <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                      value={formData.returnDate}
                      onChange={(value: string) =>
                        handleInputChange('returnDate', value)
                      }
                      placeholder="Select return date and time"
                      showTimeSelect={true}
                      className="w-full"
                      disabled={isEditMode}
                    />
                  </div>

                  {/* Return Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Return Reason <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {visibleReturnReasons.map((reason) => (
                        <label key={reason.value} className="flex items-center">
                          <input
                            type="radio"
                            name="returnReason"
                            value={reason.value}
                            checked={formData.returnReason === reason.value}
                            onChange={(e) =>
                              handleInputChange('returnReason', e.target.value)
                            }
                            className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-offset-2"
                            style={{
                              accentColor: '#5e2aac',
                            }}
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {reason.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Custom Reason Text */}
                  {formData.returnReason === 'OTHER' && (
                    <div>
                      <textarea
                        value={formData.returnReasonOther}
                        onChange={(e) =>
                          handleInputChange('returnReasonOther', e.target.value)
                        }
                        placeholder="Please specify the reason..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    </div>
                  )}
                </div>

                {/* Inspection & Checklist */}
                <div className="mt-8">
                  <div className="bg-gray-50 px-6 py-2 text-xs font-medium text-gray-400 rounded-md mb-4">
                    Inspection & Checklist
                  </div>

                  {/* Vehicle Condition */}
                  <div className="mb-6 px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vehicle Condition <span className="text-red-500">*</span>
                    </label>
                    <SearchableDropdown
                      options={vehicleConditions}
                      value={formData.returnCondition}
                      onChange={(value) =>
                        handleInputChange('returnCondition', value)
                      }
                      placeholder="e.g. Good"
                      emptyMessage="No conditions found"
                      className="w-full"
                    />
                  </div>

                  {/* List Issue */}
                  <div className="mb-6 px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      List of Issues/Damages
                    </label>
                    <textarea
                      value={formData.returnIssues}
                      onChange={(e) =>
                        handleInputChange('returnIssues', e.target.value)
                      }
                      placeholder="e.g.
Bemper Rusak 
BLDC Mati"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  {/* Vehicle Return Image */}
                  <div className="mb-6 px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Foto Kendaraan <span className="text-red-500">*</span>
                    </label>
                    <div className="">
                      {isImageUploading ? (
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <p className="mt-2 text-xs text-gray-600">
                            Uploading...
                          </p>
                        </div>
                      ) : thumbnail ? (
                        <div className="flex items-center space-x-3 p-2 border-2 border-dashed border-gray-300 rounded-lg">
                          <div className="text-gray-600">
                            <svg
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              className="w-8 h-8"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-medium text-gray-800">
                              Vehicle Return Image
                            </p>
                            <p className="text-xs text-green-600">Completed</p>
                          </div>
                          <button
                            onClick={() => {
                              setThumbnail('');
                              handleInputChange('vehicleReturnImageURL', '');
                            }}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      ) : (
                        <div className="text-center border-2 border-dashed border-gray-300 rounded-lg w-full h-32 flex flex-col items-center justify-center bg-gray-50">
                          <svg
                            className="w-8 h-8 text-gray-400 mx-auto mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <p className="text-xs text-gray-600 mb-2">
                            Choose a file or drag & drop it here.
                          </p>
                          <button
                            onClick={handleImageUpload}
                            className="text-xs bg-white border border-gray-300 text-gray-700 px-3 py-1 rounded hover:bg-gray-50"
                          >
                            Browse File
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Equipment Checklist */}
                  <div className="px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Equipment Checklist{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Uncheck item yang tidak dikembalikan.
                    </p>
                    <div className="space-y-2">
                      {formData?.unitEquipment?.map((equipment) => (
                        <label key={equipment} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData?.returnUnitEquipment?.includes(
                              equipment
                            )}
                            onChange={() => handleEquipmentToggle(equipment)}
                            className="h-4 w-4 border-gray-300 rounded focus:ring-2 focus:ring-offset-2"
                            style={{
                              accentColor: '#5e2aac',
                            }}
                            disabled={isEditMode}
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {equipment}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="bg-gray-50 px-6 py-2 text-xs font-medium text-gray-400 mb-4">
                    Return Documentation
                  </div>
                  <div className="mb-4 px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Document BAST <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.returnBastURL}
                      onChange={(e) =>
                        handleInputChange('returnBastURL', e.target.value)
                      }
                      placeholder="e.g. www.google.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Representative OEM Name */}
                  <div className="mb-4 px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nama Representative OEM{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.returnOemRepName}
                      onChange={(e) =>
                        handleInputChange('returnOemRepName', e.target.value)
                      }
                      placeholder="e.g. Mawar"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Representative OEM Phone */}
                  <div className="mb-4 px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nomor Representative OEM{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      placeholder="e.g. 081234567890"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      type="text"
                      value={
                        formData.returnOemRepPhone !== undefined
                          ? formData.returnOemRepPhone.toString()
                          : ''
                      }
                      onChange={(e) => {
                        const value = e.target.value;

                        if (value === '') {
                          handleInputChange('returnOemRepPhone', value);
                        }

                        const numericRegex = /^[0-9]*\.?[0-9]*$/;
                        if (numericRegex.test(value)) {
                          handleInputChange('returnOemRepPhone', value);
                        }
                      }}
                      onKeyPress={(e) => {
                        const allowedChars = /[0-9.]/;
                        if (
                          !allowedChars.test(e.key) &&
                          !['Backspace', 'Delete', 'Tab', 'Enter'].includes(
                            e.key
                          )
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>
                  <div className="mb-6 px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={formData.returnNotes}
                      onChange={(e) =>
                        handleInputChange('returnNotes', e.target.value)
                      }
                      placeholder="e.g. Motor dijemput oleh tim Electrum"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Both confirms are their own <dialog> opened with showModal(), so
            they stack in the top layer above this sheet — no portal needed. */}
        <ConfirmDialog
          isOpen={showConfirmModal}
          title={
            isEditMode ? 'Update return data?' : 'Return This Vehicle to OEM?'
          }
          description={
            isEditMode
              ? 'This will update the return information for this unit. Make sure all data is correct before proceeding.'
              : 'Once confirmed, this vehicle will be marked as RETURNED and removed from list of vehicles. Make sure all equipment items are verified before proceeding.'
          }
          cancelLabel="Cancel"
          confirmLabel={
            isSubmitting
              ? 'Processing...'
              : isEditMode
                ? 'Update Return Data'
                : 'Return Unit'
          }
          isBusy={isSubmitting}
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmSubmit}
        />

        {/* Here the *cancel* button is the destructive one, so Escape and the
            backdrop dismiss back into the form instead. */}
        <ConfirmDialog
          isOpen={showCancelConfirm}
          title="Are You Sure You Want To Exit?"
          description="You have entered details into the form that are not yet saved. Closing the form now will permanently lose all entered data. Are you sure you want to exit?"
          cancelLabel="Exit and Lose Data"
          confirmLabel="Stay and Continue"
          onCancel={handleClose}
          onConfirm={() => setShowCancelConfirm(false)}
          onDismiss={() => setShowCancelConfirm(false)}
        />
      </SideBarModal>
    </>
  );
};

export default ReturnVehicleModal;
